import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lumi } from '../lib/lumi';
import {Plane, Shield, Clock, Globe, CheckCircle2} from 'lucide-react';
import toast from 'react-hot-toast';

const PartnerLanding: React.FC = () => {
  const { slug: partnerSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (partnerSlug) {
      fetchPartner();
      trackVisit();
    }
  }, [partnerSlug]);

  const fetchPartner = async () => {
    try {
      const result = await lumi.entities.partners.list({
        filter: { partnerSlug, isActive: true },
        limit: 1
      });
      
      if (result.list.length > 0) {
        setPartner(result.list[0]);
      } else {
        toast.error('Partner page not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to fetch partner:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const trackVisit = async () => {
    try {
      const result = await lumi.entities.partners.list({
        filter: { partnerSlug },
        limit: 1
      });
      
      if (result.list.length > 0) {
        const currentPartner = result.list[0];
        await lumi.entities.partners.update(currentPartner._id, {
          visits: (currentPartner.visits || 0) + 1
        });
      }
    } catch (error) {
      console.error('Failed to track visit:', error);
    }
  };

  const handleCTAClick = async () => {
    if (!partner) return;

    // Track CTA click
    try {
      await lumi.entities.partners.update(partner._id, {
        ctaClicks: (partner.ctaClicks || 0) + 1
      });
    } catch (error) {
      console.error('Failed to track CTA click:', error);
    }

    // Set referral cookie (90 days)
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 90);
    document.cookie = `haven_referral=${partner._id}; expires=${expiryDate.toUTCString()}; path=/`;

    // Redirect to main Haven page with booking modal
    navigate(`/?openBooking=true&ref=${partner._id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-slate-50 to-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!partner) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-slate-50 to-stone-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 text-white">
        {/* Hero Background Image */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.pexels.com/photos/346885/pexels-photo-346885.jpeg?auto=compress&cs=tinysrgb&w=1920" 
            alt="Travel Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-navy-900/80 via-navy-800/70 to-blue-900/80"></div>
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }}></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-16 md:py-24 relative z-10">
          {/* Travel Health Visual */}
          <div className="mb-8">
            <img 
              src="https://images.pexels.com/photos/1008155/pexels-photo-1008155.jpeg?auto=compress&cs=tinysrgb&w=1200" 
              alt="Traveler exploring the world"
              className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-2xl mb-8"
            />
          </div>
          
          {/* Partner Logo & Name */}
          <div className="text-center mb-8">
            {partner.logoUrl && (
              <div className="mb-6">
                <img 
                  src={partner.logoUrl} 
                  alt={partner.partnerName}
                  className="h-20 md:h-24 mx-auto object-contain"
                />
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {partner.partnerName}
            </h1>
            <div className="flex items-center justify-center gap-2 text-cyan-400">
              <span className="text-lg">×</span>
              <span className="text-xl font-semibold">Haven Travel Health</span>
            </div>
          </div>

          {/* Welcome Message */}
          {partner.welcomeMessage && (
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-8">
              <p className="text-lg md:text-xl leading-relaxed text-center">
                {partner.welcomeMessage}
              </p>
            </div>
          )}

          {/* Main CTA */}
          <div className="text-center">
            <button
              onClick={handleCTAClick}
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-lg font-bold rounded-xl transition-all shadow-2xl hover:scale-105 hover:shadow-cyan-500/50"
            >
              <Plane className="w-6 h-6" />
              {partner.buttonLabel || 'Book Virtual Consult'}
            </button>
            <p className="mt-4 text-sm text-gray-300">
              Ages 4+ only • Virtual consultations available
            </p>
          </div>
        </div>
      </div>

      {/* Travel Imagery Section */}
      <div className="relative h-80 overflow-hidden">
        <img 
          src="https://images.pexels.com/photos/1008155/pexels-photo-1008155.jpeg?auto=compress&cs=tinysrgb&w=1920" 
          alt="Traveler with backpack"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-50 via-transparent to-transparent"></div>
      </div>

      {/* Why Travel Health Matters */}
      <div className="max-w-6xl mx-auto px-6 py-16 -mt-24 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Prepare for Your Journey
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Travel with confidence knowing you're protected with expert travel health guidance
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-cyan-600" />
            </div>
            <h3 className="font-semibold text-navy-900 mb-2">Expert Guidance</h3>
            <p className="text-gray-600 text-sm">
              Personalized recommendations from travel health specialists
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-navy-900 mb-2">Virtual & Convenient</h3>
            <p className="text-gray-600 text-sm">
              Book online and consult from anywhere at your convenience
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mb-4">
              <Globe className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-navy-900 mb-2">Global Coverage</h3>
            <p className="text-gray-600 text-sm">
              Destination-specific advice for 100+ countries worldwide
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-100 to-orange-100 rounded-xl flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="font-semibold text-navy-900 mb-2">Coordinated Care</h3>
            <p className="text-gray-600 text-sm">
              Vaccines and prescriptions arranged at local pharmacies
            </p>
          </div>
        </div>

        {/* Travel Preparation Visual */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 items-center">
          <div className="order-2 md:order-1">
            <img 
              src="https://images.pexels.com/photos/1008155/pexels-photo-1008155.jpeg?auto=compress&cs=tinysrgb&w=800" 
              alt="Travel preparation and planning"
              className="rounded-2xl shadow-2xl w-full h-auto"
            />
          </div>
          <div className="order-1 md:order-2 space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-navy-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Expert Virtual Consultations
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Connect with experienced travel health specialists from the comfort of your home. Get personalized recommendations tailored to your destination and health profile.
            </p>
            <div className="flex items-center gap-3 text-cyan-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Secure & Convenient</span>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-gradient-to-br from-white to-stone-50 rounded-2xl p-8 md:p-12 shadow-lg border border-gray-200">
          <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            What's Included in Your Consultation
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-navy-900 mb-1">Pre-Trip Assessment</h4>
                <p className="text-gray-600 text-sm">Review of your itinerary, health history, and vaccine records</p>
              </div>
            </div>
            <div className="flex gap-4">
              <CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-navy-900 mb-1">Vaccine Recommendations</h4>
                <p className="text-gray-600 text-sm">Destination-specific vaccine and medication guidance</p>
              </div>
            </div>
            <div className="flex gap-4">
              <CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-navy-900 mb-1">Prescription Coordination</h4>
                <p className="text-gray-600 text-sm">Prescriptions sent directly to your chosen pharmacy</p>
              </div>
            </div>
            <div className="flex gap-4">
              <CheckCircle2 className="w-6 h-6 text-cyan-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-navy-900 mb-1">Travel Health Record</h4>
                <p className="text-gray-600 text-sm">Digital vaccination record and trip-specific safety tips</p>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-12">
          <button
            onClick={handleCTAClick}
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-lg font-bold rounded-xl transition-all shadow-2xl hover:scale-105 hover:shadow-cyan-500/50"
          >
            <Plane className="w-6 h-6" />
            {partner.buttonLabel || 'Book Virtual Consult'}
          </button>
          <p className="mt-4 text-sm text-gray-500">
            Virtual Individual Travel Consultation: $60 CAD
          </p>
        </div>
      </div>

      {/* Destinations Showcase */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h3 className="text-2xl md:text-3xl font-bold text-navy-900 mb-8 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Popular Destinations We Cover
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="relative h-48 rounded-xl overflow-hidden group cursor-pointer">
              <img 
                src="https://images.pexels.com/photos/2387418/pexels-photo-2387418.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Southeast Asia"
                className="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                <span className="text-white font-semibold">Southeast Asia</span>
              </div>
            </div>
            <div className="relative h-48 rounded-xl overflow-hidden group cursor-pointer">
              <img 
                src="https://images.pexels.com/photos/2132126/pexels-photo-2132126.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Africa"
                className="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                <span className="text-white font-semibold">Africa</span>
              </div>
            </div>
            <div className="relative h-48 rounded-xl overflow-hidden group cursor-pointer">
              <img 
                src="https://images.pexels.com/photos/3278215/pexels-photo-3278215.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Latin America"
                className="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                <span className="text-white font-semibold">Latin America</span>
              </div>
            </div>
            <div className="relative h-48 rounded-xl overflow-hidden group cursor-pointer">
              <img 
                src="https://images.pexels.com/photos/2166559/pexels-photo-2166559.jpeg?auto=compress&cs=tinysrgb&w=600" 
                alt="Middle East"
                className="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-4">
                <span className="text-white font-semibold">Middle East</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="relative bg-gradient-to-br from-navy-900 to-navy-800 text-white py-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.pexels.com/photos/1125260/pexels-photo-1125260.jpeg?auto=compress&cs=tinysrgb&w=1920" 
            alt="World map"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <p className="text-lg mb-4">
            Trusted by travelers heading to 100+ destinations worldwide
          </p>
          <p className="text-gray-400 text-sm">
            Book with Dr. Saba Baig, PharmD in partnership with VoyageVax
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerLanding;
